import React, { type ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface HolidaySectionProps {
    /** Anchor target for the sticky nav tabs. */
    id: string
    title?: string
    children: ReactNode
    isMobile: boolean
    /**
     * `card` — its own bordered card.
     * `plain` — sits on the page background (listing page).
     * `flat` — no chrome of its own: the detail page nests every section inside one shared card,
     *          so each section contributes only a rule between itself and the previous one.
     */
    variant: 'card' | 'plain' | 'flat'
    /** `flat` only: suppresses the leading divider on the first section in the container. */
    isFirst?: boolean
    /** Content above the title, e.g. the package grid's filter and sort. */
    beforeTitle?: ReactNode
}

const HolidaySection: React.FC<HolidaySectionProps> = ({
    id,
    title,
    children,
    isMobile,
    variant,
    isFirst = false,
    beforeTitle
}) => {
    const isCard = variant === 'card'
    const isFlat = variant === 'flat'

    const titleNode = title ? (
        <Typography
            component="h2"
            sx={{
                fontSize: isMobile ? '1.25rem' : '1.625rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'textColors.primary'
            }}
        >
            {title}
        </Typography>
    ) : null

    return (
        <Box
            component="section"
            id={id}
            sx={{
                // Keeps the heading clear of the sticky tab bar when the anchor is jumped to.
                scrollMarginTop: isMobile ? '132px' : '124px',
                ...(isCard && {
                    p: isMobile ? 2 : 3,
                    borderRadius: HOLIDAYS_RADIUS.SURFACE,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'borders.default'
                }),
                ...(isFlat && {
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 2.5 : 3,
                    ...(isFirst ? {} : { borderTop: '1px solid', borderColor: 'borders.default' })
                })
            }}
        >
            <Stack spacing={isMobile ? 1.75 : 2.25}>
                {(title || beforeTitle) && (
                    <Box>
                        {beforeTitle && <Box sx={{ mb: isMobile ? 2 : 2.5 }}>{beforeTitle}</Box>}
                        {titleNode}
                    </Box>
                )}
                {children}
            </Stack>
        </Box>
    )
}

export { HolidaySection }
export type { HolidaySectionProps }
export default HolidaySection
